import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { register, setToken, setStoredUser, checkEmail, verifyCode, uploadKycDocument, checkSiteUrlPublic, fetchApi } from '../api';
import { useAuth } from '../App';
import { REFERRAL_SOURCES, parseReferralValue, buildReferralValue } from '../utils/referralSources';

// Toggle pour réactiver l'inscription investisseur — masquage temporaire, code conservé.
const INVESTOR_REGISTRATION_ENABLED = false;
// Toggle pour réactiver l'étape de dépôt des documents KYC — masquage temporaire, code conservé.
const KYC_DOCS_ENABLED = false;
// Mode démo : simule l'envoi du code de vérification email tant que le serveur SMTP n'est pas configuré.
// Real email verification — the backend (kyc.py) sends a 6-digit code via
// SMTP using SMTP_FROM (contact.tropheeslbpam@investance-partners.com by
// default). Set this to true to fall back to a hardcoded "123456" demo code
// when there is no SMTP server reachable from the dev environment.
const EMAIL_VERIFICATION_SIMULATED = false;
const DEMO_VERIFICATION_CODE = '123456';

const STEPS_STARTUP = [
  { key: 'role', label: 'Profil' },
  { key: 'identity', label: 'Identité' },
  { key: 'email', label: 'Email pro' },
  { key: 'company', label: 'Startup' },
  ...(KYC_DOCS_ENABLED ? [{ key: 'documents', label: 'Documents KYC' }] : []),
];
const STEPS_INVESTOR = [
  { key: 'role', label: 'Profil' },
  { key: 'identity', label: 'Identité' },
  { key: 'email', label: 'Email pro' },
  { key: 'company', label: 'Société' },
  ...(KYC_DOCS_ENABLED ? [{ key: 'documents', label: 'Documents KYC' }] : []),
];

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const initialRole = (INVESTOR_REGISTRATION_ENABLED && searchParams.get('role') === 'investisseur') ? 'investisseur' : 'startup';
  const [role, setRole] = useState(initialRole);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    email: '', password: '', confirm: '', first_name: '', last_name: '', company: '', phone: '',
    startup_name: '', secteur: 'Fintech', site_web: '', siret: '', siren: '', maturite: '< 24 mois', adressage_client: 'B2B',
    referral_source: '', consent_score_visible: true, consent_visible: true,
  });
  const [kyc, setKyc] = useState({
    emailChecked: false, emailIsPro: false, emailCode: '', emailVerified: false,
    idDocType: 'carte_identite', idDocUploaded: false, idDocFilename: '',
    companyDocType: 'kbis', companyDocUploaded: false, companyDocFilename: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [urlCheck, setUrlCheck] = useState({ checking: false, result: null });
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Validation téléphone français : formats acceptés +33 6 12 34 56 78, 0612345678, etc.
  const isValidPhone = (phone) => {
    if (!phone) return false;
    const cleaned = phone.replace(/[\s.-]/g, '');
    return /^(?:\+33|0)[1-9]\d{8}$/.test(cleaned);
  };

  // Normalize site_web on blur
  const normalizeSiteWeb = (raw) => {
    if (!raw) return raw;
    let url = raw.trim();
    const localMatch = url.match(/^https?:\/\/localhost[:\d]*\/(?:startup|startups|detail)\/?(.+)/i);
    if (localMatch) url = localMatch[1];
    if (/^https?:\/\/localhost/i.test(url)) return '';
    if (!/^https?:\/\//i.test(url) && url.includes('.')) {
      if (!url.startsWith('www.')) url = 'www.' + url;
      url = 'https://' + url;
    }
    return url;
  };

  const handleSiteWebBlur = () => {
    const normalized = normalizeSiteWeb(form.site_web);
    if (normalized !== form.site_web) {
      upd('site_web', normalized);
    }
    setUrlCheck({ checking: false, result: null });
  };

  const handleCheckUrl = async () => {
    if (!form.site_web) return;
    setUrlCheck({ checking: true, result: null });
    try {
      const res = await checkSiteUrlPublic(form.site_web);
      setUrlCheck({ checking: false, result: res });
      if (res.normalized_url && res.normalized_url !== form.site_web) {
        upd('site_web', res.normalized_url);
      }
    } catch (err) {
      setUrlCheck({ checking: false, result: { accessible: false, reason: err.message } });
    }
  };

  const handleSiretLookup = async () => {
    const siret = (form.siret || '').replace(/[^0-9]/g, '');
    if (siret.length < 9) { setError('Le SIRET doit contenir au moins 9 chiffres'); return; }
    setLookingUp(true);
    setLookupResult(null);
    try {
      // Call French government API directly (no auth needed)
      const query = siret.substring(0, 9);
      const resp = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${query}&page=1&per_page=5`);
      const res = await resp.json();
      if (res.results && res.results.length > 0) {
        const r = res.results[0];
        const siege = r.siege || {};
        const match = {
          nom_complet: r.nom_complet || r.nom_raison_sociale || '',
          siret: siege.siret || '',
          siren: r.siren || '',
        };
        setLookupResult(match);
        if (!form.startup_name && match.nom_complet) upd('startup_name', match.nom_complet);
        if (match.siret) upd('siret', match.siret);
        if (match.siren) upd('siren', match.siren);
      } else {
        setLookupResult({ not_found: true });
      }
    } catch (err) { setError('Erreur de recherche : ' + err.message); }
    setLookingUp(false);
  };

  const updKyc = (k, v) => setKyc(s => ({ ...s, [k]: v }));

  const steps = role === 'startup' ? STEPS_STARTUP : STEPS_INVESTOR;

  // Step validations
  const canNext = () => {
    if (step === 0) return true; // role selection
    if (step === 1) return form.first_name && form.last_name && isValidPhone(form.phone) && form.password && form.password === form.confirm && form.password.length >= 6;
    if (step === 2) {
      // Email pro vérifié OU email non-pro accepté (validation admin différée)
      if (kyc.emailVerified) return true;
      if (kyc.emailChecked && !kyc.emailIsPro && !kyc.emailAlreadyUsed) return true;
      return false;
    }
    if (step === 3) {
      if (role === 'startup') {
        return !!(form.startup_name && form.siret && form.siret.replace(/\s/g,'').length >= 9
          && form.secteur && form.maturite && form.site_web && form.adressage_client && form.referral_source);
      }
      return !!form.company;
    }
    if (step === 4) return kyc.idDocUploaded && kyc.companyDocUploaded;
    return false;
  };

  const handleCheckEmail = async () => {
    setError('');
    try {
      const res = await checkEmail(form.email);
      updKyc('emailChecked', true);
      updKyc('emailIsPro', res.is_professional);
      updKyc('emailAlreadyUsed', !!res.already_used);
      if (res.already_used) {
        // Bloquer immédiatement : email déjà utilisé.
        updKyc('emailIsPro', false);
        setError(res.message);
        return;
      }
      // Email non-pro : on n affiche plus une erreur, on laissera l UI proposer
      // de continuer avec validation admin différée.
      if (!EMAIL_VERIFICATION_SIMULATED && res.is_professional && res.email_sent === false) {
        setError('Le serveur de messagerie n\'est pas configuré. Contactez l\'administrateur.');
      }
    } catch (err) {
      if (EMAIL_VERIFICATION_SIMULATED) {
        // Mode démo : on suppose que l'email est pro pour ne pas bloquer.
        updKyc('emailChecked', true);
        updKyc('emailIsPro', true);
        updKyc('emailAlreadyUsed', false);
      } else {
        setError(err.message);
      }
    }
  };

  const handleResendCode = async () => {
    setEmailSending(true);
    setError('');
    try {
      const res = await fetchApi('/kyc/resend-code', { method: 'POST', body: JSON.stringify({ email: form.email }) });
      if (!EMAIL_VERIFICATION_SIMULATED && res.email_sent === false) {
        setError('Le serveur de messagerie n\'est pas configuré. Contactez l\'administrateur.');
      }
    } catch (err) {
      if (!EMAIL_VERIFICATION_SIMULATED) setError(err.message);
    }
    setEmailSending(false);
  };

  const handleVerifyCode = async () => {
    setError('');
    if (EMAIL_VERIFICATION_SIMULATED) {
      if (kyc.emailCode === DEMO_VERIFICATION_CODE) {
        updKyc('emailVerified', true);
      } else {
        setError('Code de vérification incorrect (mode démo : utilisez ' + DEMO_VERIFICATION_CODE + ')');
      }
      return;
    }
    try {
      const res = await verifyCode(form.email, kyc.emailCode);
      if (res.verified) {
        updKyc('emailVerified', true);
      } else {
        setError('Code de vérification incorrect');
      }
    } catch (err) { setError(err.message); }
  };

  const handleUploadDoc = async (docKey, docType) => {
    setError('');
    try {
      const fakeName = docType === 'carte_identite' ? 'CNI_recto_verso.pdf' :
        docType === 'passeport' ? 'Passeport.pdf' :
        docType === 'kbis' ? 'Kbis_extrait.pdf' :
        docType === 'delegation' ? 'Delegation_pouvoir.pdf' : 'Attestation.pdf';
      await uploadKycDocument({ document_type: docType, filename: fakeName, file_size: 245000 });
      updKyc(`${docKey}Uploaded`, true);
      updKyc(`${docKey}Filename`, fakeName);
    } catch (err) { setError(err.message); }
  };

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      // Pass the email_is_pro flag so the backend can flag the account
      // for admin manual validation when appropriate.
      const res = await register({ ...form, role, email_is_pro: kyc.emailIsPro !== false });
      setToken(res.token);
      setStoredUser(res.user);
      setUser(res.user);
      navigate(role === 'startup' ? '/startup/dashboard' : '/search');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const renderStepIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32, justifyContent: 'center' }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && <div style={{ width: 40, height: 2, background: i <= step ? 'var(--accent)' : 'var(--border)' }} />}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: i < step ? 'pointer' : 'default' }}
            onClick={() => { if (i < step) setStep(i); }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700,
              background: i < step ? 'var(--accent)' : i === step ? 'var(--primary)' : 'var(--border)',
              color: i <= step ? 'white' : 'var(--text-light)' }}>
              {i < step ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 10, color: i <= step ? 'var(--primary)' : 'var(--text-light)', fontWeight: i === step ? 600 : 400, whiteSpace: 'nowrap' }}>
              {s.label}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
      <div style={{ width: '100%', maxWidth: 620, padding: 24 }}>
        <div className="card" style={{ padding: 40 }}>
          <h1 style={{ fontSize: 22, marginBottom: 4, textAlign: 'center' }}>Créer un compte</h1>
          <p style={{ textAlign: 'center', color: 'var(--text-light)', marginBottom: 24, fontSize: 13 }}>
            Plateforme ISI — Informations du compte
          </p>

          {renderStepIndicator()}

          {error && <div style={{ background: '#FFEBEE', color: '#C62828', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}

          {/* ===== STEP 0: Role ===== */}
          {step === 0 && (
            <div>
              <h2 style={{ fontSize: 18, marginBottom: 16 }}>Choisissez votre profil</h2>
              <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                {[
                  { r: 'startup', icon: '🚀', label: 'Startup', desc: 'Vous représentez une startup et souhaitez candidater aux trophées de l\'innovation 2026 ou apparaître dans l\'Officiel 2026 des fintech françaises', disabled: false },
                  { r: 'investisseur', icon: '💼', label: 'Investisseur', desc: 'Vous souhaitez consulter et investir dans des startups', disabled: !INVESTOR_REGISTRATION_ENABLED },
                ].map(opt => {
                  const isSelected = role === opt.r;
                  return (
                    <button key={opt.r} onClick={() => { if (opt.disabled) return; setRole(opt.r); setError(''); }}
                      disabled={opt.disabled} aria-disabled={opt.disabled}
                      title={opt.disabled ? 'Profil bientôt disponible' : undefined}
                      style={{ flex: 1, padding: '20px 16px', position: 'relative',
                        border: `2px solid ${isSelected ? 'var(--primary-light)' : 'var(--border)'}`,
                        borderRadius: 12, background: isSelected ? '#E3F2FD' : 'white',
                        cursor: opt.disabled ? 'not-allowed' : 'pointer',
                        opacity: opt.disabled ? 0.55 : 1,
                        textAlign: 'left', fontFamily: 'inherit' }}>
                      {opt.disabled && (
                        <span style={{ position: 'absolute', top: 10, right: 10, fontSize: 10, fontWeight: 700,
                          background: 'var(--text-light)', color: 'white', padding: '3px 8px', borderRadius: 999, letterSpacing: 0.5 }}>
                          BIENTÔT
                        </span>
                      )}
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{opt.icon}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: isSelected ? 'var(--primary)' : 'var(--text)', marginBottom: 4 }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-light)', lineHeight: 1.5 }}>{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== STEP 1: Identity ===== */}
          {step === 1 && (
            <div>
              <h2 style={{ fontSize: 18, marginBottom: 16 }}>Informations personnelles</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div><label className="form-label">Prénom *</label>
                  <input type="text" value={form.first_name} onChange={e => upd('first_name', e.target.value)} required className="form-input" placeholder="Votre prénom" /></div>
                <div><label className="form-label">Nom *</label>
                  <input type="text" value={form.last_name} onChange={e => upd('last_name', e.target.value)} required className="form-input" placeholder="Votre nom" /></div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="form-label">Téléphone *</label>
                <input type="tel" name="isi-phone-no-autofill" value={form.phone}
                  onChange={e => upd('phone', e.target.value)} required
                  autoComplete="off" data-lpignore="true" data-form-type="other"
                  className="form-input" placeholder="" />
                {form.phone && !isValidPhone(form.phone) && (
                  <div style={{ color: '#E74C3C', fontSize: 12, marginTop: 6 }}>
                    Numéro invalide. Formats acceptés : +33 6 12 34 56 78 ou 06 12 34 56 78.
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label className="form-label">Mot de passe *</label>
                  <input type="password" name="isi-new-password" value={form.password}
                    onChange={e => upd('password', e.target.value)} required
                    autoComplete="new-password" data-lpignore="true" data-form-type="other"
                    className="form-input" placeholder="Min. 6 caractères" /></div>
                <div><label className="form-label">Confirmation *</label>
                  <input type="password" name="isi-confirm-password" value={form.confirm}
                    onChange={e => upd('confirm', e.target.value)} required
                    autoComplete="new-password" data-lpignore="true" data-form-type="other"
                    className="form-input" /></div>
              </div>
              {form.password && form.confirm && form.password !== form.confirm && (
                <div style={{ color: '#E74C3C', fontSize: 12, marginTop: 6 }}>Les mots de passe ne correspondent pas</div>
              )}
              <div style={{ marginTop: 16, padding: 12, background: '#FFF8E1', borderRadius: 8, fontSize: 12, color: '#795548' }}>
                <strong>Important :</strong> Le prénom et le nom saisis doivent correspondre exactement à ceux figurant sur {role === 'startup' ? 'le Kbis de votre société' : 'votre attestation d\'habilitation'}.
              </div>
            </div>
          )}

          {/* ===== STEP 2: Email verification ===== */}
          {step === 2 && (
            <div>
              <h2 style={{ fontSize: 18, marginBottom: 4 }}>Vérification de l'email professionnel</h2>
              <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20 }}>
                Une adresse email professionnelle (domaine de votre entreprise) est requise pour valider votre inscription.
              </p>

              <div style={{ marginBottom: 16 }}>
                <label className="form-label">Email professionnel *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="email" value={form.email} onChange={e => { upd('email', e.target.value); updKyc('emailChecked', false); updKyc('emailVerified', false); updKyc('emailAlreadyUsed', false); }}
                    className="form-input" placeholder="prenom.nom@votre-entreprise.com" style={{ flex: 1 }} />
                  <button onClick={handleCheckEmail} className="btn btn-primary" disabled={!form.email.includes('@')}
                    style={{ whiteSpace: 'nowrap', fontSize: 12, padding: '8px 16px' }}>
                    Vérifier
                  </button>
                </div>
              </div>

              {kyc.emailChecked && (
                <div style={{ marginBottom: 16, padding: 12, borderRadius: 8,
                  background: kyc.emailIsPro ? '#E8F5E9' : '#FFF3E0',
                  border: `1px solid ${kyc.emailIsPro ? '#A5D6A7' : '#FFCC80'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ fontSize: 18 }}>{kyc.emailIsPro ? '✅' : '⚠️'}</span>
                    <span>{kyc.emailIsPro ? 'Domaine professionnel détecté' : 'Domaine de messagerie gratuit détecté'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>
                    Domaine : <strong>{form.email.split('@')[1]}</strong>
                  </div>
                </div>
              )}

              {/* Email non-pro accepté : pas de code, validation admin différée */}
              {kyc.emailChecked && !kyc.emailIsPro && !kyc.emailAlreadyUsed && (
                <div style={{ padding: 16, background: '#E3F2FD', borderRadius: 8,
                  border: '1px solid #90CAF9', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: 22 }}>ℹ️</span>
                    <div>
                      <strong style={{ color: '#1565C0' }}>Validation par un administrateur</strong>
                      <p style={{ fontSize: 13, color: '#1565C0', margin: '6px 0 0', lineHeight: 1.5 }}>
                        L&apos;adresse <strong>{form.email}</strong> n&apos;appartient pas à un domaine professionnel.
                        Vous pouvez tout de même poursuivre votre inscription : un administrateur vérifiera
                        manuellement votre compte avant son activation. Vous serez notifié par email à la
                        validation.
                      </p>
                      <p style={{ fontSize: 12, color: '#1976D2', margin: '8px 0 0', fontStyle: 'italic' }}>
                        Cliquez sur « Suivant » pour continuer.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {kyc.emailChecked && kyc.emailIsPro && !kyc.emailVerified && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 12, padding: 10, background: '#E3F2FD', borderRadius: 6, fontSize: 12, color: '#1565C0' }}>
                    Un code de vérification a été envoyé à votre adresse email.
                  </div>
                  {EMAIL_VERIFICATION_SIMULATED && (
                    <div style={{ marginBottom: 12, padding: 10, background: '#FFF8E1', borderRadius: 6, fontSize: 12, color: '#795548', border: '1px dashed #FFB300' }}>
                      <strong>Mode démo :</strong> le serveur de messagerie n'est pas encore configuré. Utilisez le code <strong>{DEMO_VERIFICATION_CODE}</strong> pour valider cette étape.
                    </div>
                  )}
                  <label className="form-label">Code de vérification reçu par email</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" value={kyc.emailCode} onChange={e => updKyc('emailCode', e.target.value)}
                      className="form-input" placeholder="000000" maxLength={6} style={{ maxWidth: 160, textAlign: 'center', letterSpacing: 4, fontSize: 18, fontWeight: 700 }} />
                    <button onClick={handleVerifyCode} className="btn btn-accent" disabled={kyc.emailCode.length !== 6}
                      style={{ fontSize: 12, padding: '8px 16px' }}>Valider</button>
                    <button onClick={handleResendCode} className="btn btn-outline" disabled={emailSending}
                      style={{ fontSize: 12, padding: '8px 16px', whiteSpace: 'nowrap' }}>
                      {emailSending ? 'Envoi...' : 'Renvoyer le code'}
                    </button>
                  </div>
                </div>
              )}

              {kyc.emailVerified && (
                <div style={{ padding: 16, background: '#E8F5E9', borderRadius: 8, border: '1px solid #A5D6A7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 24 }}>✅</span>
                    <div>
                      <strong style={{ color: '#2E7D32' }}>Email vérifié avec succès</strong>
                      <div style={{ fontSize: 12, color: '#558B2F', marginTop: 2 }}>{form.email}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 3: Company info ===== */}
          {step === 3 && (
            <div>
              <h2 style={{ fontSize: 18, marginBottom: 16 }}>
                {role === 'startup' ? 'Informations sur la startup' : 'Informations société'}
              </h2>

              {role === 'startup' ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label className="form-label">Nom de la startup *</label>
                    <input type="text" value={form.startup_name} onChange={e => upd('startup_name', e.target.value)} required className="form-input" />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label className="form-label">SIRET * <span style={{ fontSize: 11, color: 'var(--text-light)', fontWeight: 400 }}>(obligatoire — 14 chiffres)</span></label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="text" value={form.siret} onChange={e => { upd('siret', e.target.value.replace(/[^0-9]/g, '').substring(0, 14)); setLookupResult(null); }}
                        required className="form-input" placeholder="Numéro SIRET à 14 chiffres"
                        style={{ fontFamily: 'monospace', letterSpacing: 1, flex: 1 }} />
                      <button type="button" onClick={handleSiretLookup} disabled={!form.siret || form.siret.replace(/\s/g, '').length < 9 || lookingUp}
                        style={{ padding: '8px 14px', fontSize: 11, background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', opacity: (!form.siret || form.siret.replace(/\s/g, '').length < 9 || lookingUp) ? 0.5 : 1 }}>
                        {lookingUp ? '⏳ Recherche...' : '🔍 Rechercher'}
                      </button>
                    </div>
                    {form.siret && form.siret.length > 0 && form.siret.length < 9 && (
                      <div style={{ fontSize: 11, color: '#E74C3C', marginTop: 4 }}>Le SIRET doit contenir au moins 9 chiffres (SIREN)</div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>
                      Vous pouvez trouver votre SIRET sur votre extrait Kbis ou sur <a href="https://annuaire-entreprises.data.gouv.fr" target="_blank" rel="noopener noreferrer">annuaire-entreprises.data.gouv.fr</a>
                    </div>
                  </div>
                  {lookupResult && !lookupResult.not_found && (
                    <div style={{ marginBottom: 12, padding: 12, background: '#E8F5E9', borderRadius: 8, fontSize: 13 }}>
                      <strong style={{ color: '#2E7D32' }}>{'✓'} Informations récupérées depuis le RCS</strong>
                      <div style={{ marginTop: 6, color: 'var(--text-light)' }}>
                        <strong>{lookupResult.nom_complet}</strong> — SIREN {lookupResult.siren}
                        {lookupResult.etat_administratif === 'A' ?
                          <span style={{ fontSize: 10, marginLeft: 8, padding: '2px 6px', background: '#4CAF50', color: '#fff', borderRadius: 4 }}>Active</span> :
                          <span style={{ fontSize: 10, marginLeft: 8, padding: '2px 6px', background: '#F44336', color: '#fff', borderRadius: 4 }}>Fermée</span>
                        }
                        {lookupResult.adresse && <div style={{ marginTop: 4, fontSize: 12 }}>Adresse : {lookupResult.adresse}</div>}
                        {lookupResult.dirigeants && lookupResult.dirigeants.length > 0 && (
                          <div style={{ marginTop: 4, fontSize: 12 }}>Dirigeant(s) : {lookupResult.dirigeants.map(d => `${d.prenoms || ''} ${d.nom || ''}`).join(', ')}</div>
                        )}
                      </div>
                    </div>
                  )}
                  {lookupResult && lookupResult.not_found && (
                    <div style={{ marginBottom: 12, padding: 12, background: '#FFF3E0', borderRadius: 8, fontSize: 13, color: '#E65100' }}>
                      Aucune entreprise trouvée pour ce SIRET. Vérifiez le numéro saisi.
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div><label className="form-label">Secteur d'activité *</label>
                      <select value={form.secteur} onChange={e => upd('secteur', e.target.value)} required className="form-input">
                        {['Fintech', 'Insurtech', 'Regtech', 'Biotech', 'Greentech', 'Medtech', 'SaaS', 'HealthTech', 'Immotech'].map(s =>
                          <option key={s} value={s}>{s}</option>)}</select></div>
                    <div><label className="form-label">Maturité *</label>
                      <select value={form.maturite} onChange={e => upd('maturite', e.target.value)} required className="form-input">
                        {['< 6 mois', '< 12 mois', '< 18 mois', '< 24 mois', '< 36 mois', '> 36 mois'].map(m =>
                          <option key={m} value={m}>{m}</option>)}</select></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div><label className="form-label">Site web *</label>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="url" value={form.site_web} onChange={e => { upd('site_web', e.target.value); setUrlCheck({ checking: false, result: null }); }} onBlur={handleSiteWebBlur} required className="form-input" placeholder="https://www.monsite.com" style={{ flex: 1 }} />
                        <button type="button" onClick={handleCheckUrl} disabled={!form.site_web || urlCheck.checking}
                          style={{ padding: '8px 12px', fontSize: 11, background: urlCheck.result?.accessible ? '#4CAF50' : 'var(--primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', opacity: (!form.site_web || urlCheck.checking) ? 0.5 : 1 }}>
                          {urlCheck.checking ? '⏳' : urlCheck.result?.accessible ? '✓ OK' : '🔗 Tester'}
                        </button>
                      </div>
                      {urlCheck.result && (
                        <div style={{ marginTop: 4, fontSize: 11, color: urlCheck.result.accessible ? '#2E7D32' : '#C62828', padding: '4px 8px', background: urlCheck.result.accessible ? '#E8F5E9' : '#FFEBEE', borderRadius: 4 }}>
                          {urlCheck.result.accessible ? `✓ Site accessible (${urlCheck.result.status_code})` : `✗ ${urlCheck.result.reason || 'Site inaccessible'}`}
                        </div>
                      )}
                    </div>
                    <div><label className="form-label">Clientèle *</label>
                      <select value={form.adressage_client} onChange={e => upd('adressage_client', e.target.value)} required className="form-input">
                        <option value="B2B">B2B</option><option value="B2C">B2C</option><option value="B2B2C">B2B2C</option></select></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label className="form-label">Comment avez-vous entendu parler de l'événement ? *</label>
                      {(() => {
                        const { option, otherText } = parseReferralValue(form.referral_source);
                        return (
                          <>
                            <select value={option} onChange={e => upd('referral_source', buildReferralValue(e.target.value, otherText))} required className="form-input">
                              <option value="">-- Sélectionnez --</option>
                              {REFERRAL_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            {option === 'Autre' && (
                              <input type="text" value={otherText} required
                                onChange={e => upd('referral_source', buildReferralValue('Autre', e.target.value))}
                                placeholder="Précisez..."
                                className="form-input" style={{ marginTop: 8 }} />
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div>
                      <label className="form-label">Consentement - Visibilité du score ISI *</label>
                      <select value={form.consent_score_visible} onChange={e => upd('consent_score_visible', e.target.value === 'true')} required className="form-input">
                        <option value="true">Je consens à rendre visible mon score ISI auprès des investisseurs</option>
                        <option value="false">Je ne consens pas à rendre visible mon score ISI aux investisseurs</option>
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label className="form-label">Nom de la société *</label>
                    <input type="text" value={form.company} onChange={e => upd('company', e.target.value)} required className="form-input" />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label className="form-label">Thèse d'investissement</label>
                    <textarea value={form.investment_thesis || ''} onChange={e => upd('investment_thesis', e.target.value)}
                      className="form-input" rows={3} style={{ resize: 'vertical' }}
                      placeholder="Décrivez vos critères d'investissement, secteurs cibles, tickets..." />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div><label className="form-label">Secteurs préférés</label>
                      <input type="text" value={form.preferred_sectors || ''} onChange={e => upd('preferred_sectors', e.target.value)} className="form-input"
                        placeholder="Fintech, Insurtech..." /></div>
                    <div><label className="form-label">Stades cibles</label>
                      <input type="text" value={form.preferred_stages || ''} onChange={e => upd('preferred_stages', e.target.value)} className="form-input"
                        placeholder="Amorçage, Série A..." /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                    <div>
                      <label className="form-label">Comment avez-vous entendu parler de l'événement ?</label>
                      {(() => {
                        const { option, otherText } = parseReferralValue(form.referral_source);
                        return (
                          <>
                            <select value={option} onChange={e => upd('referral_source', buildReferralValue(e.target.value, otherText))} className="form-input">
                              <option value="">-- Sélectionnez --</option>
                              {REFERRAL_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            {option === 'Autre' && (
                              <input type="text" value={otherText}
                                onChange={e => upd('referral_source', buildReferralValue('Autre', e.target.value))}
                                placeholder="Précisez..."
                                className="form-input" style={{ marginTop: 8 }} />
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div>
                      <label className="form-label">Consentement - Visibilité</label>
                      <select value={form.consent_visible} onChange={e => upd('consent_visible', e.target.value === 'true')} className="form-input">
                        <option value="true">Je consens à me rendre visible auprès des startups</option>
                        <option value="false">Je ne consens pas à me rendre visible auprès des startups</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== STEP 4: Document upload ===== */}
          {step === 4 && (
            <div>
              <h2 style={{ fontSize: 18, marginBottom: 4 }}>Pièces justificatives KYC</h2>
              <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20 }}>
                {role === 'startup'
                  ? 'Téléchargez votre pièce d\'identité et un extrait Kbis de moins de 3 mois. Le nom du dirigeant sur le Kbis doit correspondre à votre pièce d\'identité.'
                  : 'Téléchargez votre pièce d\'identité et un document justifiant votre habilitation à agir au nom de votre société (délégation de pouvoir ou attestation).'}
              </p>

              {/* ID Document */}
              <div style={{ padding: 16, border: `2px solid ${kyc.idDocUploaded ? '#A5D6A7' : 'var(--border)'}`, borderRadius: 12, marginBottom: 16,
                background: kyc.idDocUploaded ? '#F1F8E9' : 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <h3 style={{ fontSize: 15, margin: 0 }}>1. Pièce d'identité</h3>
                    <p style={{ fontSize: 12, color: 'var(--text-light)', margin: '4px 0 0' }}>Carte nationale d'identité ou passeport en cours de validité</p>
                  </div>
                  {kyc.idDocUploaded && <span style={{ fontSize: 20 }}>✅</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <select value={kyc.idDocType} onChange={e => updKyc('idDocType', e.target.value)} className="form-input" style={{ maxWidth: 220, fontSize: 13 }}>
                    <option value="carte_identite">Carte d'identité (recto/verso)</option>
                    <option value="passeport">Passeport</option>
                  </select>
                </div>
                {!kyc.idDocUploaded ? (
                  <button onClick={() => handleUploadDoc('idDoc', kyc.idDocType)} className="btn btn-primary" style={{ fontSize: 13 }}>
                    Télécharger le document (démo)
                  </button>
                ) : (
                  <div style={{ fontSize: 12, color: '#2E7D32' }}>
                    Fichier : {kyc.idDocFilename} — Téléchargé ✓
                  </div>
                )}
              </div>

              {/* Company Document */}
              <div style={{ padding: 16, border: `2px solid ${kyc.companyDocUploaded ? '#A5D6A7' : 'var(--border)'}`, borderRadius: 12,
                background: kyc.companyDocUploaded ? '#F1F8E9' : 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <h3 style={{ fontSize: 15, margin: 0 }}>
                      2. {role === 'startup' ? 'Extrait Kbis (< 3 mois)' : 'Justificatif d\'habilitation'}
                    </h3>
                    <p style={{ fontSize: 12, color: 'var(--text-light)', margin: '4px 0 0' }}>
                      {role === 'startup'
                        ? 'Le nom du dirigeant sur le Kbis sera vérifié avec votre pièce d\'identité'
                        : 'Délégation de pouvoir ou attestation prouvant votre habilitation'}
                    </p>
                  </div>
                  {kyc.companyDocUploaded && <span style={{ fontSize: 20 }}>✅</span>}
                </div>
                {role === 'investisseur' && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <select value={kyc.companyDocType} onChange={e => updKyc('companyDocType', e.target.value)} className="form-input" style={{ maxWidth: 260, fontSize: 13 }}>
                      <option value="delegation">Délégation de pouvoir</option>
                      <option value="attestation">Attestation d'habilitation</option>
                      <option value="kbis">Extrait Kbis</option>
                    </select>
                  </div>
                )}
                {!kyc.companyDocUploaded ? (
                  <button onClick={() => handleUploadDoc('companyDoc', role === 'startup' ? 'kbis' : kyc.companyDocType)}
                    className="btn btn-primary" style={{ fontSize: 13 }}>
                    Télécharger le document (démo)
                  </button>
                ) : (
                  <div style={{ fontSize: 12, color: '#2E7D32' }}>
                    Fichier : {kyc.companyDocFilename} — Téléchargé ✓
                  </div>
                )}
              </div>

              {kyc.idDocUploaded && kyc.companyDocUploaded && (
                <div style={{ marginTop: 16, padding: 12, background: '#E3F2FD', borderRadius: 8, fontSize: 12, color: '#1565C0' }}>
                  <strong>Mode démo :</strong> En production, un système OCR extraira automatiquement les noms depuis vos documents pour vérifier la concordance entre la pièce d'identité et le {role === 'startup' ? 'Kbis' : 'justificatif d\'habilitation'}.
                </div>
              )}
            </div>
          )}

          {/* Navigation buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
            {step > 0 ? (
              <button onClick={() => { setStep(step - 1); setError(''); }} className="btn btn-outline" style={{ fontSize: 14 }}>
                ← Précédent
              </button>
            ) : <div />}

            {step < steps.length - 1 ? (
              <button onClick={() => { if (canNext()) { setStep(step + 1); setError(''); } }}
                className="btn btn-primary" disabled={!canNext()} style={{ fontSize: 14 }}>
                Suivant →
              </button>
            ) : (
              <button onClick={handleSubmit} className="btn btn-accent" disabled={loading || !canNext()}
                style={{ fontSize: 14, padding: '12px 32px' }}>
                {loading ? 'Inscription...' : 'Finaliser l\'inscription'}
              </button>
            )}
          </div>

          {/* KYC Summary at last step */}
          {step === steps.length - 1 && (
            <div style={{ marginTop: 20, padding: 16, background: '#F5F5F5', borderRadius: 8, fontSize: 12, color: 'var(--text-light)' }}>
              <strong>Récapitulatif :</strong>
              <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
                <div>✅ Email professionnel vérifié : <strong>{form.email}</strong></div>
                {KYC_DOCS_ENABLED && (
                  <>
                    <div>✅ Pièce d'identité déposée : <strong>{kyc.idDocFilename}</strong></div>
                    <div>✅ {role === 'startup' ? 'Kbis' : 'Justificatif'} déposé : <strong>{kyc.companyDocFilename}</strong></div>
                    <div style={{ marginTop: 4, color: '#E65100' }}>
                      ⏳ La validation de votre dossier KYC sera effectuée sous 48h ouvrées par l'équipe Investance Partners.
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/login" style={{ fontSize: 13 }}>Déjà un compte ? Se connecter</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
